import React from "react";
import { useNavigate } from "react-router-dom";
import { ArrowRight, Home, Sparkles, Search } from "lucide-react";
import { createAnonAxios, ensureAnonSession } from "@/lib/vg2AnonAxios";
import { getEffectiveRef } from "@/lib/refTracker";

const API = createAnonAxios(
  `${process.env.REACT_APP_BACKEND_URL}/api/v2`,
);

// Deterministic mapping suggestion → canonical C_objective code.
// Reuses the six values already defined in journey_config.py; no second
// taxonomy is introduced. "Altro" is used only when no direction fits.
export const HOMEPAGE_SUGGESTIONS = [
  { key: "sell",       label: "Vendere e capire quando conviene",         code: "vendere" },
  { key: "value",      label: "Sapere quanto vale e quale potenziale ha", code: "capire_quanto_vale" },
  { key: "income",     label: "Creare una rendita",                       code: "generare_reddito" },
  { key: "inherited",  label: "Decidere cosa fare di una casa ereditata", code: "capire_quale_scelta_conviene" },
  { key: "renovate",   label: "Ristrutturare o valorizzare l'immobile",   code: "capire_quale_scelta_conviene" },
  { key: "costs",      label: "Trovare una soluzione ai costi continui della casa", code: "capire_quale_scelta_conviene" },
  { key: "guide",      label: "Non so ancora cosa fare: guidami",         code: "altro" },
];


async function ensureAnonToken() {
  return ensureAnonSession(`${process.env.REACT_APP_BACKEND_URL}/api/v2`);
}


export default function OwnerHomeHero() {
  const navigate = useNavigate();
  const [text, setText] = React.useState("");
  const [selected, setSelected] = React.useState(null); // suggestion key
  const [open, setOpen] = React.useState(false);
  const [busy, setBusy] = React.useState(false);
  const [error, setError] = React.useState(null);
  const inputRef = React.useRef(null);
  const menuRef = React.useRef(null);
  const [activeIndex, setActiveIndex] = React.useState(-1);

  const trimmed = text.trim();
  const matched = HOMEPAGE_SUGGESTIONS.find((s) => s.label === trimmed);
  const filteredSuggestions = trimmed
    ? HOMEPAGE_SUGGESTIONS.filter((s) =>
        s.label.toLowerCase().includes(trimmed.toLowerCase()))
    : HOMEPAGE_SUGGESTIONS;
  const showFreeText = trimmed.length >= 4 && !matched;
  const canStart = !busy && (matched || (trimmed.length >= 4 && trimmed.length <= 2000));

  const startWithSuggestion = (s) => {
    setSelected(s.key);
    setText(s.label);
    setOpen(false);
    setActiveIndex(-1);
    inputRef.current?.blur();
  };

  const onKeyDown = (e) => {
    if (!open && (e.key === "ArrowDown" || e.key === "Enter")) {
      setOpen(true);
      setActiveIndex(0);
      e.preventDefault();
      return;
    }
    if (!open) return;
    const rowsCount = filteredSuggestions.length + (showFreeText ? 1 : 0);
    if (e.key === "ArrowDown") {
      setActiveIndex((i) => (i + 1) % Math.max(1, rowsCount));
      e.preventDefault();
    } else if (e.key === "ArrowUp") {
      setActiveIndex((i) => (i - 1 + rowsCount) % Math.max(1, rowsCount));
      e.preventDefault();
    } else if (e.key === "Escape") {
      setOpen(false);
      setActiveIndex(-1);
    } else if (e.key === "Enter") {
      // If a menu row is highlighted, pick it. Otherwise submit.
      if (activeIndex >= 0 && activeIndex < filteredSuggestions.length) {
        startWithSuggestion(filteredSuggestions[activeIndex]);
        e.preventDefault();
        return;
      }
      if (activeIndex === filteredSuggestions.length && showFreeText) {
        setOpen(false);
        setActiveIndex(-1);
        e.preventDefault();
        return;
      }
      submit();
      e.preventDefault();
    }
  };

  const submit = async () => {
    if (busy) return;
    if (!canStart) return;
    setBusy(true); setError(null);
    try {
      await ensureAnonToken();
      const body = matched
        ? { initial_objective: matched.code, initial_owner_intent_text: matched.label }
        : { initial_owner_intent_text: trimmed };
      const r = await API.post("/journeys/capisci/start", { ...body, referral_code_origine: getEffectiveRef() });
      navigate(`/vg2/${r.data.journey_id}`);
    } catch (e) {
      const msg = e?.response?.data?.detail?.message
        || "Non riesco ad avviare il percorso. Riprova.";
      setError(msg);
      setBusy(false);
    }
  };

  // Close menu on outside click.
  React.useEffect(() => {
    const onDoc = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)
          && inputRef.current && !inputRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  return (
    <div data-testid="owner-home-hero-tool"
         className="relative w-full">
      <div ref={menuRef} className="relative">
        <p className="mb-3 text-sm md:text-base text-[#0A0A0A]/80"
           data-testid="owner-home-hero-prompt">
          Inserisci qui una breve descrizione del tuo progetto immobiliare.
        </p>
        <div className={
          "flex items-stretch rounded-xl border-2 bg-white shadow-[0_20px_50px_-25px_rgba(0,0,0,0.15)] " +
          "border-[#E5E7EB] focus-within:border-[#B40101] transition-colors"
        }>
          <div className="hidden md:flex items-center pl-4 pr-1 text-[#B40101]">
            <Search className="w-5 h-5" aria-hidden />
          </div>
          <textarea
            ref={inputRef}
            rows={text.length > 80 ? 4 : 1}
            value={text}
            onChange={(e) => { setText(e.target.value); setSelected(null); }}
            onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); submit(); } }}
            maxLength={2000}
            minLength={4}
            placeholder="Descrizione libera..."
            data-testid="owner-home-hero-input"
            aria-label="Descrizione libera del tuo progetto immobiliare"
            className="flex-1 min-w-0 px-4 md:px-3 py-4 md:py-5 text-base md:text-lg outline-none rounded-l-xl resize-none bg-transparent"
          />
          <button
            type="button"
            onClick={submit}
            disabled={!canStart}
            data-testid="owner-home-hero-submit"
            className="shrink-0 flex items-center gap-2 px-5 md:px-8 bg-[#B40101] text-white text-base md:text-lg font-semibold rounded-r-xl disabled:opacity-40 hover:bg-[#8f0101] transition-colors"
          >
            {busy ? "…" : "Inizia"}
            <ArrowRight className="w-4 h-4" />
          </button>
        </div>
        <p className="mt-3 text-xs md:text-sm text-[#4B5563]"
           data-testid="owner-home-hero-microcopy">
          Le tue informazioni restano private.
        </p>
      </div>
      {error && (
        <p role="alert" data-testid="owner-home-hero-error"
           className="mt-3 text-sm text-[#B40101]">{error}</p>
      )}
    </div>
  );
}


/** Small helper: navigate to homepage hero with an intent already
 *  filled (used by the situation cards). */
export function useStartWithIntent() {
  const navigate = useNavigate();
  return React.useCallback(async (code, label) => {
    try {
      await ensureAnonToken();
      const r = await API.post("/journeys/capisci/start", {
        initial_objective: code,
        initial_owner_intent_text: label,
        referral_code_origine: getEffectiveRef(),
      });
      navigate(`/vg2/${r.data.journey_id}`);
    } catch (e) {
      // Fallback: send them to the entry page.
      navigate("/vg2");
    }
  }, [navigate]);
}
